import type { Sparte } from '@comparit/common-api';
import { DestroyRef, Injectable, inject } from '@angular/core';
import type { ProviderToken, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl } from '@angular/forms';
import { isEqual } from '@comparit/core';
import { TaskContextService, firstInvalidStep } from '@comparit/shared';
import type { CalculatorForm, MarkedItem } from '@comparit/shared';
import type { Observable } from 'rxjs';
import {
  BehaviorSubject,
  NEVER,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { BerechnungHubClient } from './berechnung-hub-client.service';
import type { ErgebnisInput } from '../interfaces/ergebnis-input.model';

/** The funnel emits on every keystroke; a run is only worth starting once the inputs settle. */
const DEBOUNCE_MS = 400;

/**
 * The engine behind the results page, Sparte-agnostic: the trigger that starts a run whenever the
 * funnel's Vorgaben settle while the page is open, over the `BerechnungHubClient` that runs it.
 * Same shape as `SchutzOptimierenKatalogBaseService`: the app subclass supplies its generated
 * types and form state, this lib names no generated type.
 */
@Injectable()
export abstract class ResultsBaseService<TVorgaben, TErgebnis extends ErgebnisInput> {
  /** The DI token can't carry the subclass's TErgebnis; the generic says what the hub delivers. */
  private readonly hub = inject(
    BerechnungHubClient as ProviderToken<BerechnungHubClient<TErgebnis>>
  );
  private readonly taskContext = inject(TaskContextService);
  private readonly destroyRef = inject(DestroyRef);
  private started = false;

  /** The hub's Sparte argument. The app supplies this, so it takes the generated enum. */
  protected abstract readonly sparte: Sparte;

  /** The funnel as the Sparte wants it — `formsService.berechnungFormularValue$`. */
  protected abstract readonly vorgaben$: Observable<TVorgaben>;

  /** Every step's form group; a run never starts while one is invalid. */
  protected abstract readonly allFgs: Record<string, AbstractControl>;

  /** The funnel's base route; the invalid-inputs dialog sends the customer to `${formularUrl}/stepN`. */
  abstract readonly formularUrl: string;

  /** Where the navigator's back link goes — normally the funnel's last step. */
  abstract readonly backUrl: string;

  /** Offered by the navigator while tariffs are marked; unset until the Sparte has a Vergleich page. */
  readonly forwardUrl?: string;

  /** Compare / Bausteine / sort state, persisted by the app's FormsService. */
  abstract readonly calculatorForm: CalculatorForm;

  /** Caption above a premium, e.g. "Jährlicher Beitrag", for the Zahlungsweise the run was priced with. */
  abstract readonly beitragCaption: Signal<string>;

  /** Insurer logo as base64 SVG by BaFin number. An arrow property, so the `map` pipe can call it unbound. */
  abstract readonly findVersichererLogo$: (bafinRegNr: string) => Observable<string | undefined>;

  /** Emits when the customer starts over; the next identical Vorgaben are then calculated again. */
  protected readonly journeyReset$: Observable<void> = NEVER;

  /** The Vorgaben minus what the backend stores but does not price; two runs equal on this are one. */
  protected pricingIdentity(vorgaben: TVorgaben): unknown {
    return vorgaben;
  }

  readonly allResults$ = this.hub.allResults$;
  readonly all = this.hub.all;
  readonly berechnungResponse = this.hub.berechnungResponse;
  readonly currentNumberOfErgebnisse = this.hub.currentNumberOfErgebnisse;
  readonly hasError = this.hub.hasError;
  readonly noResults = this.hub.noResults;
  /** Each batch the hub delivers, as it arrives. */
  readonly ergebnisReceived = this.hub.ergebnisReceived;

  private readonly inCalculatorPage$ = new BehaviorSubject(false);

  /** Idempotent; the app shell calls it once so the trigger outlives any single page. */
  init(): void {
    if (this.started) return;
    this.started = true;

    this.journeyReset$
      .pipe(
        startWith(undefined),
        switchMap(() =>
          combineLatest([this.vorgaben$, this.inCalculatorPage$.pipe(distinctUntilChanged())]).pipe(
            filter(([, inCalculatorPage]) => inCalculatorPage),
            debounceTime(DEBOUNCE_MS),
            filter(() => !this.isCalculationFormInvalid()),
            distinctUntilChanged(([prev], [curr]) =>
              isEqual(this.pricingIdentity(prev), this.pricingIdentity(curr))
            ),
            switchMap(([vorgaben]) => this.calculate(vorgaben))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  enterCalculatorPage(): void {
    this.inCalculatorPage$.next(true);
  }

  leaveCalculatorPage(): void {
    this.inCalculatorPage$.next(false);
  }

  isCalculationFormInvalid(): boolean {
    return Object.values(this.allFgs).some(fg => fg.invalid);
  }

  /** True whenever the funnel is incomplete; the page shows the invalid-inputs dialog on it. */
  formInvalid$(): Observable<boolean> {
    return this.vorgaben$.pipe(
      startWith(null),
      map(() => this.isCalculationFormInvalid()),
      distinctUntilChanged()
    );
  }

  /** The earliest invalid step's url, or the Formular itself when every step is valid. */
  firstInvalidStepUrl(): string {
    const step = firstInvalidStep(this.allFgs, Number.MAX_SAFE_INTEGER);
    return step ? `${this.formularUrl}/${step}` : this.formularUrl;
  }

  /** Searches the current run only. */
  findTarif$ = (
    identifier: Pick<MarkedItem, 'berechnungId' | 'kombinationId'>
  ): Observable<TErgebnis | undefined> =>
    this.allResults$.pipe(
      map(ergebnisse =>
        ergebnisse.find(
          item =>
            item.kombinationId === identifier.kombinationId &&
            item.berechnungId === identifier.berechnungId
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  private calculate(vorgaben: TVorgaben): Observable<TErgebnis[]> {
    const { compare, bausteine } = this.calculatorForm.ctrls;
    const oldCompare = [...compare.value];
    const oldBausteine = [...bausteine.value];

    bausteine.setValue([]);
    compare.setValue([]);

    void this.hub.start(this.sparte, {
      berechnungvorgaben: vorgaben,
      taskId: this.taskContext.taskId() ?? undefined,
    });

    return this.ergebnisReceived.pipe(
      tap(received => {
        this.reconcileMarkedItems(oldCompare, received, item =>
          this.calculatorForm.toggleCompare(item)
        );
        this.reconcileMarkedItems(oldBausteine, received, item =>
          this.calculatorForm.toggleBausteine(item)
        );
      })
    );
  }

  /** Re-marks what the customer had marked, under the new run's berechnungId. */
  private reconcileMarkedItems(
    oldItems: MarkedItem[],
    ergebnisse: TErgebnis[],
    toggle: (item: MarkedItem) => void
  ): void {
    for (const item of oldItems) {
      const found = ergebnisse.find(r => r.kombinationId === item.kombinationId && r.successful);
      if (found) {
        toggle({ ...item, berechnungId: found.berechnungId });
      }
    }
  }
}
